/*
 * Opening hours in the browser. The only script copy of the rules; the only
 * Liquid copy is snippets/opening.liquid, and the two are kept in step by a
 * shared table of test moments. Change a rule in both.
 *
 * Liquid prints each place's state as the page is built, and prints the
 * timetable once as JSON (snippets/opening.liquid, want: 'data'). A page can
 * be cached, or sit open across 8am or 6pm, so this works every state out
 * again for the visitor's moment: on load, each minute, and when the tab is
 * shown again. Text is only written when it has changed, so a fresh page
 * does not shift.
 *
 * The moment is always New Zealand time, read with Intl, never the
 * visitor's own clock: a customer in Sydney sees the farm's day, not theirs.
 *
 * What it keeps right, by attribute:
 *
 *   data-opening="shop|coffee|ruby|pyo|orders"
 *     One place. The element gets data-opening-tone (open, later, closed;
 *     for orders open, soon, last, closed) and data-opening-state. On it or
 *     inside it:
 *       data-opening-text="short|full"   the words, as text
 *       data-opening-title               the full sentence, as the title
 *       data-opening-class="open:a later:b closed:c"
 *                                        the class for the tone in force;
 *                                        a state name works as well as a tone
 *
 *   data-opening-week="shop|coffee|ruby|pyo"
 *     A week list. Each row carries data-opening-day="0..6" (0 is Sunday)
 *     and gets aria-current="date" on today. Inside a row,
 *     data-opening-hours is the hours text and data-opening-late is shown
 *     only on the late night. data-opening-closed-class on the list names
 *     the class a closed day's hours take.
 *
 *   data-opening-note                    the sentence under a week
 *   data-opening-season="in_season|summer|christmas|closed"
 *                                        aria-current="true" on the one in force
 *
 * Other modules can import { opening, nzMoment } to ask the same timetable
 * for a place's hours on a date or for the online orders settings.
 */

const ZONE = 'Pacific/Auckland';
const PLACES = ['shop', 'coffee', 'ruby', 'pyo'];
const DAY_MS = 86400000;
const MONDAY_FIRST = [1, 2, 3, 4, 5, 6, 0];
// How far ahead a closed place looks for its next opening.
const REACH = 21;

const nzClock = new Intl.DateTimeFormat('en-NZ', {
  timeZone: ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/**
 * A moment in New Zealand: the date as one number (20261006) and the
 * minutes since midnight.
 * @param {Date} [date]
 * @returns {{ ymd: number, min: number }}
 */
export function nzMoment(date = new Date()) {
  const part = {};
  nzClock.formatToParts(date).forEach((p) => {
    part[p.type] = Number(p.value);
  });
  return {
    ymd: part.year * 10000 + part.month * 100 + part.day,
    min: (part.hour % 24) * 60 + part.minute,
  };
}

// Dates are stepped as calendar days in UTC, where no day is ever 23 or 25
// hours long. Nothing here reads the visitor's time zone.
function utc(ymd) {
  return Date.UTC(Math.floor(ymd / 10000), (Math.floor(ymd / 100) % 100) - 1, ymd % 100);
}

function addDays(ymd, days) {
  const date = new Date(utc(ymd) + days * DAY_MS);
  return date.getUTCFullYear() * 10000 + (date.getUTCMonth() + 1) * 100 + date.getUTCDate();
}

function weekday(ymd) {
  return new Date(utc(ymd)).getUTCDay();
}

function isoDate(ymd) {
  const text = String(ymd);
  return `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`;
}

function fill(line, values) {
  return String(line || '').replace(/\{(\w+)\}/g, (match, key) => values[key] ?? match);
}

export class Opening {
  /** @param {object} data The timetable printed by snippets/opening.liquid. */
  constructor(data) {
    this.data = data;
    this.words = data.words;

    const [opens, summer, last] = data.season;
    this.seasonOpens = opens;
    this.seasonOn = opens > 0 && last > 0;
    // Days are ranked from the day the season opens, so a season that runs
    // across the new year stays in order: January ranks after December.
    this.rankLast = this.seasonOn && last < opens ? last + 1300 : last;
    this.rankSummer = summer < opens ? summer + 1300 : summer;
    this.summerOn = this.seasonOn && summer > 0 && this.rankSummer <= this.rankLast;

    [this.xmasFrom, this.xmasBack] = data.christmas;
    this.xmasOn = this.xmasFrom > 0 && this.xmasBack > 0 && this.xmasFrom !== this.xmasBack;
    [this.pickingFirst, this.pickingLast] = data.picking;
  }

  inBreak(md) {
    if (!this.xmasOn) return false;
    if (this.xmasFrom < this.xmasBack) return md >= this.xmasFrom && md < this.xmasBack;
    return md >= this.xmasFrom || md < this.xmasBack;
  }

  /**
   * One date: whether the farm is in the Christmas break (X), closed for
   * the season (S) or neither (o), which week applies, and each place's
   * cell: [open, close, late], 'x' for closed that day, and for picking
   * 'b' before the first picking day or 'a' after the last.
   */
  day(ymd) {
    const md = ymd % 10000;
    const md4 = String(md).padStart(4, '0');
    const dow = weekday(ymd);
    const rank = md < this.seasonOpens ? md + 1300 : md;

    let status = 'o';
    if (this.inBreak(md)) status = 'X';
    else if (this.seasonOn && rank > this.rankLast) status = 'S';
    const pat = this.summerOn && status !== 'S' && rank >= this.rankSummer ? 1 : 0;

    const shut = (who) => this.data.closed.includes(`|${ymd}:${who}|`) || this.data.closed.includes(`|*${md4}:${who}|`);
    const farmClosed = shut('a');
    const cells = PLACES.map((place, pl) => {
      let cell = this.data.weeks[pat * 28 + dow * 4 + pl] || 'x';
      if (farmClosed || (pl > 0 && shut(pl))) cell = 'x';
      if (pl === 3) {
        if (this.pickingFirst > 0 && ymd < this.pickingFirst) cell = 'b';
        else if (this.pickingLast > 0 && ymd > this.pickingLast) cell = 'a';
      }
      return cell;
    });
    return { status, pat, cells, ymd, dow };
  }

  time(minutes) {
    if (minutes === 720) return this.words.midday;
    if (minutes === 0 || minutes === 1440) return this.words.midnight;
    const hour = Math.floor(minutes / 60);
    const minute = minutes % 60;
    return `${hour % 12 || 12}${minute ? `:${String(minute).padStart(2, '0')}` : ''}${hour >= 12 ? 'pm' : 'am'}`;
  }

  shortDate(ymd) {
    return `${ymd % 100} ${this.words.months[(Math.floor(ymd / 100) % 100) - 1]}`;
  }

  longDate(ymd) {
    return `${this.words.days[weekday(ymd)]} ${this.shortDate(ymd)}`;
  }

  /**
   * "Back open Wednesday 13 January": the first day from the end of the
   * break that the farm shop has hours, so a reopening date that lands on a
   * closed day of the week is never promised.
   */
  reopening(ymd) {
    const year = Math.floor(ymd / 10000);
    const md = ymd % 10000;
    // The break in question is the one running, or else the next to start.
    let breakYear = year;
    if (this.inBreak(md)) {
      if (this.xmasFrom > this.xmasBack && md < this.xmasBack) breakYear = year - 1;
    } else if (md > this.xmasFrom) {
      breakYear = year + 1;
    }
    if (this.xmasFrom > this.xmasBack) breakYear += 1;
    const back = breakYear * 10000 + this.xmasBack;
    for (let i = 0; i < 7; i += 1) {
      const day = this.day(addDays(back, i));
      if (day.status === 'o' && day.cells[0] !== 'x') return day.ymd;
    }
    return back;
  }

  /**
   * Online orders. The last Dispatch Day really offered is the last dispatch
   * day on or before the date typed that is not a no-dispatch date; the
   * first, the same going forward. The last ordering day is the day before
   * the last one, and "Closing soon" is the five days before that.
   */
  dispatchEdge(typed, step) {
    const { days, none } = this.data.orders;
    for (let i = 0; i < 14; i += 1) {
      const ymd = addDays(typed, i * step);
      if (days.includes(`,${weekday(ymd)},`) && !none.includes(`|${ymd}|`)) return ymd;
    }
    return typed;
  }

  ordersState(at) {
    const { first, last, cutoff } = this.data.orders;
    const w = this.words;
    const cutoffText = this.time(cutoff * 60);
    let state = 'open';
    let lastOrder = 0;
    if (last > 0) {
      lastOrder = addDays(this.dispatchEdge(last, -1), -1);
      if (at.ymd > lastOrder) state = 'closed';
      else if (at.ymd === lastOrder) state = at.min >= cutoff * 60 ? 'closed' : 'last';
      else if (at.ymd >= addDays(lastOrder, -5)) state = 'soon';
    }
    const firstDispatch = first > 0 ? this.dispatchEdge(first, 1) : 0;
    if (state === 'open' && firstDispatch > at.ymd) state = 'first';

    switch (state) {
      case 'closed':
        return { tone: 'closed', state, short: w.closed, full: w.ordersClosed };
      case 'last':
        return { tone: 'last', state, short: w.lastOrders, full: fill(w.ordersLast, { time: cutoffText }) };
      case 'soon':
        return {
          tone: 'soon',
          state,
          short: w.closingSoon,
          full: fill(w.ordersSoon, { time: cutoffText, date: this.longDate(lastOrder) }),
        };
      case 'first':
        return { tone: 'open', state, short: w.open, full: fill(w.ordersFirst, { date: this.longDate(firstDispatch) }) };
      default:
        return { tone: 'open', state, short: w.open, full: fill(w.ordersOpen, { days: this.data.text.orderDays }) };
    }
  }

  /**
   * What a place says at a moment. The ladder, first that answers wins:
   * the Christmas break, closed for the season, picking's own dates, then
   * the day's hours; a closed place looks ahead for its next opening.
   * @param {string} place shop, coffee, ruby, pyo or orders
   * @param {{ ymd: number, min: number }} [at]
   * @returns {{ tone: string, state: string, short: string, full: string }}
   */
  state(place, at = nzMoment()) {
    if (place === 'orders') return this.ordersState(at);
    const pl = PLACES.indexOf(place);
    if (pl < 0) return { tone: 'closed', state: '', short: '', full: '' };
    if (pl === 2 && this.data.ruby === false) return { tone: 'closed', state: 'away', short: '', full: '' };

    const w = this.words;
    const today = this.day(at.ymd);
    const cell = today.cells[pl];
    let state = '';
    let timeText = '';
    let dayText = '';

    if (today.status === 'X') state = 'christmas';
    else if (today.status === 'S') state = 'season';
    else if (cell === 'b') state = 'pyo_before';
    else if (cell === 'a') state = 'pyo_after';
    else {
      if (cell !== 'x') {
        if (at.min < cell[0]) {
          state = 'later';
          timeText = this.time(cell[0]);
        } else if (at.min < cell[1]) {
          state = cell[2] ? 'late' : 'open';
          timeText = this.time(cell[1]);
        }
      }
      if (!state) {
        // Closed now. Walk forward to the next opening; the break, the end
        // of the season or the end of picking may come first.
        for (let i = 1; i <= REACH; i += 1) {
          const next = this.day(addDays(at.ymd, i));
          if (next.status === 'X') state = 'christmas';
          else if (next.status === 'S') state = 'season';
          else if (next.cells[pl] === 'a') state = 'pyo_after';
          else if (next.cells[pl] === 'x' || next.cells[pl] === 'b') continue;
          else {
            state = 'closed';
            timeText = this.time(next.cells[pl][0]);
            if (i === 1) dayText = w.tomorrow;
            // further off than a week, a day name alone would mislead
            else dayText = i < 8 ? w.days[next.dow] : this.longDate(next.ymd);
          }
          break;
        }
        if (cell === 'x') {
          if (state === 'closed' || !state) state = 'closed_day';
        } else if (!state) {
          state = 'closed';
        }
      }
    }

    switch (state) {
      case 'open':
      case 'late': {
        let full = fill(state === 'late' ? w.openLate : w.openUntil, { time: timeText });
        if (pl === 0) {
          // The farm shop's sentence carries the coffee cart's.
          const coffee = today.cells[1];
          let coffeeLine = w.coffeeClosed;
          if (coffee !== 'x') {
            if (at.min < coffee[0]) coffeeLine = fill(w.coffeeFrom, { time: this.time(coffee[0]) });
            else if (at.min < coffee[1]) coffeeLine = fill(w.coffeeUntil, { time: this.time(coffee[1]) });
          }
          full = `${full} ${coffeeLine}`;
        }
        return { tone: 'open', state, short: w.open, full };
      }
      case 'later':
        return {
          tone: 'later',
          state,
          short: fill(w.opens, { time: timeText }),
          full: fill(w.opensToday, { time: timeText }),
        };
      case 'closed':
      case 'closed_day': {
        const again = timeText ? fill(w.openAgain, { day: dayText, time: timeText }) : '';
        const full = state === 'closed_day' ? `${w.closedToday} ${again}`.trim() : again;
        return { tone: 'closed', state, short: w.closed, full };
      }
      case 'christmas':
        return {
          tone: 'closed',
          state,
          short: w.christmas,
          full: fill(w.christmasFull, { date: this.longDate(this.reopening(at.ymd)) }),
        };
      case 'season':
        return { tone: 'closed', state, short: w.closed, full: fill(w.season, { date: this.data.text.seasonOpens }) };
      case 'pyo_before':
        return {
          tone: 'closed',
          state,
          short: w.closed,
          full: fill(w.pyoBefore, { date: this.longDate(this.pickingFirst) }),
        };
      default:
        return { tone: 'closed', state, short: w.closed, full: w.pyoAfter };
    }
  }

  hoursText(cell) {
    return fill(this.words.hours, { open: this.time(cell[0]), close: this.time(cell[1]) });
  }

  /**
   * The week in force at a moment, Monday first: the summer week from the
   * day summer hours start. Today is marked only while the week is running.
   */
  week(place, at = nzMoment()) {
    const pl = Math.max(PLACES.indexOf(place), 0);
    const today = this.day(at.ymd);
    return MONDAY_FIRST.map((dow) => {
      const cell = this.data.weeks[today.pat * 28 + dow * 4 + pl] || 'x';
      const closed = cell === 'x';
      return {
        dow,
        name: this.words.days[dow],
        hours: closed ? this.words.weekClosed : this.hoursText(cell),
        today: dow === today.dow && today.status === 'o',
        late: !closed && cell[2] === 1,
        closed,
      };
    });
  }

  note(at = nzMoment()) {
    return this.day(at.ymd).pat === 1 ? this.data.text.noteSummer : this.data.text.noteSeason;
  }

  season(at = nzMoment()) {
    const today = this.day(at.ymd);
    if (today.status === 'X') return 'christmas';
    if (today.status === 'S') return 'closed';
    return today.pat === 1 ? 'summer' : 'in_season';
  }

  /**
   * The days ahead a place is open, from tomorrow.
   * @returns {{ iso: string, hours: string, open: number, close: number }[]}
   */
  upcoming(place, days, at = nzMoment()) {
    const pl = Math.max(PLACES.indexOf(place), 0);
    const list = [];
    for (let i = 1; i <= days; i += 1) {
      const day = this.day(addDays(at.ymd, i));
      const cell = day.cells[pl];
      if (day.status !== 'o' || typeof cell === 'string') continue;
      list.push({ iso: isoDate(day.ymd), hours: this.hoursText(cell), open: cell[0], close: cell[1] });
    }
    return list;
  }

  /**
   * The online orders settings, in the shapes the dispatch calendar reads:
   * weekday names, ISO dates, and the hour of the day before by which an
   * order must be in (24 is midnight).
   */
  get orders() {
    const { days, first, last, noneIso, cutoff } = this.data.orders;
    return {
      days: MONDAY_FIRST.filter((dow) => days.includes(`,${dow},`)).map((dow) => this.words.dayKeys[dow]),
      first: first > 0 ? isoDate(first) : '',
      last: last > 0 ? isoDate(last) : '',
      none: noneIso ? noneIso.split(',') : [],
      cutoffHour: cutoff,
    };
  }
}

let instance;
let tryAt = null;

/**
 * The page's timetable, or null on a page that printed none.
 * @returns {Opening | null}
 */
export function opening() {
  if (instance !== undefined) return instance;
  instance = null;
  if (typeof document === 'undefined') return instance;
  const source = document.querySelector('script[data-opening-timetable]');
  if (!source) return instance;
  try {
    instance = new Opening(JSON.parse(source.textContent));
  } catch (error) {
    instance = null;
  }
  return instance;
}

function setText(element, text) {
  if (element.textContent !== text) element.textContent = text;
}

function setAttribute(element, name, value) {
  if (value === null) {
    if (element.hasAttribute(name)) element.removeAttribute(name);
  } else if (element.getAttribute(name) !== value) {
    element.setAttribute(name, value);
  }
}

// The scope itself and what sits inside it, leaving out anything that
// belongs to a scope nested deeper.
function within(scope, selector) {
  const found = Array.from(scope.querySelectorAll(selector)).filter(
    (element) => element.parentElement.closest('[data-opening]') === scope
  );
  if (scope.matches(selector)) found.unshift(scope);
  return found;
}

/**
 * Works every marked element out again for the moment. Safe on a page with
 * none.
 * @param {ParentNode} [root]
 */
export function paint(root = document) {
  const timetable = opening();
  if (!timetable) return;
  const at = tryAt || nzMoment();

  root.querySelectorAll('[data-opening]').forEach((scope) => {
    const result = timetable.state(scope.dataset.opening, at);
    if (!result.state || result.state === 'away') return;
    setAttribute(scope, 'data-opening-tone', result.tone);
    setAttribute(scope, 'data-opening-state', result.state);
    within(scope, '[data-opening-text]').forEach((element) => {
      const text = result[element.dataset.openingText];
      if (typeof text === 'string') setText(element, text);
    });
    within(scope, '[data-opening-title]').forEach((element) => {
      setAttribute(element, 'title', result.full || null);
    });
    within(scope, '[data-opening-class]').forEach((element) => {
      element.dataset.openingClass.split(/\s+/).forEach((pair) => {
        const cut = pair.indexOf(':');
        if (cut < 1) return;
        const when = pair.slice(0, cut);
        element.classList.toggle(pair.slice(cut + 1), when === result.tone || when === result.state);
      });
    });
  });

  root.querySelectorAll('[data-opening-week]').forEach((list) => {
    const rows = timetable.week(list.dataset.openingWeek, at);
    const closedClass = list.dataset.openingClosedClass;
    list.querySelectorAll('[data-opening-day]').forEach((element) => {
      const row = rows.find((candidate) => String(candidate.dow) === element.dataset.openingDay);
      if (!row) return;
      setAttribute(element, 'aria-current', row.today ? 'date' : null);
      const hours = element.querySelector('[data-opening-hours]');
      if (hours) {
        setText(hours, row.hours);
        if (closedClass) hours.classList.toggle(closedClass, row.closed);
      }
      const late = element.querySelector('[data-opening-late]');
      if (late && late.hidden === row.late) late.hidden = !row.late;
    });
  });

  root.querySelectorAll('[data-opening-note]').forEach((element) => {
    setText(element, timetable.note(at));
  });

  const season = timetable.season(at);
  root.querySelectorAll('[data-opening-season]').forEach((element) => {
    setAttribute(element, 'aria-current', element.dataset.openingSeason === season ? 'true' : null);
  });
}

// The theme editor's "Try another day and time". The markup comes from
// snippets/opening.liquid and only exists there in the editor. The moment
// picked is kept for the editing session so it survives the preview
// reloading after a setting changes; it never reaches a customer.
function editor() {
  // The panel belongs to the layout, once. Should a section print it again,
  // the copies would sit on top of the one that is wired up.
  document.querySelectorAll('[data-opening-editor]').forEach((panel, index) => {
    if (index > 0) panel.remove();
  });
  const control = document.querySelector('[data-opening-try]');
  if (!control) return;
  const input = control.querySelector('[data-opening-try-at]');
  const reset = control.querySelector('[data-opening-try-reset]');
  const KEY = 'opening-try-at';

  const read = (value) => {
    const found = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || '');
    if (!found) return null;
    const [, year, month, day, hour, minute] = found.map(Number);
    return { ymd: year * 10000 + month * 100 + day, min: hour * 60 + minute };
  };
  const remember = (value) => {
    try {
      if (value) sessionStorage.setItem(KEY, value);
      else sessionStorage.removeItem(KEY);
    } catch (error) {
      // a browser that blocks storage still gets the control, for this page only
    }
  };

  let saved = '';
  try {
    saved = sessionStorage.getItem(KEY) || '';
  } catch (error) {
    saved = '';
  }
  tryAt = read(saved);
  if (tryAt) {
    input.value = saved;
    control.open = true;
  }

  input.addEventListener('input', () => {
    tryAt = read(input.value);
    remember(tryAt ? input.value : '');
    paint();
  });
  reset.addEventListener('click', () => {
    tryAt = null;
    input.value = '';
    remember('');
    paint();
  });
}

function start() {
  if (!opening()) return;
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);
  if (designMode) editor();
  paint();

  // Timed to land just after each minute turns, so 8:00 flips at 8:00.
  const tick = () => {
    paint();
    setTimeout(tick, 60000 - (Date.now() % 60000) + 250);
  };
  setTimeout(tick, 60000 - (Date.now() % 60000) + 250);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) paint();
  });
  // a page restored from the back-forward cache keeps its old text
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) paint();
  });
  if (designMode) {
    document.addEventListener('shopify:section:load', () => paint());
  }
}

if (typeof document !== 'undefined') start();
